import { AppConstants } from '@/app.constants';
import { openDB, IDBPDatabase } from 'idb';

interface StoreConfig {
  keyPath?: string | string[];
  autoIncrement?: boolean;
  indexes?: Array<{
    name: string;
    keyPath: string | string[];
    options?: IDBIndexParameters;
  }>;
}

interface DBStores {
  [key: string]: StoreConfig;
}

export const ensureDB = async (): Promise<IDBPDatabase> => {
  try {
    return await openDB(AppConstants.INDEX_DB.dbName, AppConstants.INDEX_DB.version, {
      upgrade(db) {
        const stores = Object.entries(AppConstants.INDEX_DB.store as DBStores);
        const storeConfig = AppConstants.INDEX_DB.storeConfig ?? {};

        stores.forEach(([storeName, config]) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const keyPath = storeConfig[storeName]?.keyPath ?? config.keyPath;
            const store = db.createObjectStore(storeName, {
              keyPath,
              autoIncrement: config.autoIncrement,
            });

            // Create indexes if defined
            config.indexes?.forEach((index) => {
              store.createIndex(index.name, index.keyPath, index.options);
            });

            console.log(`Created store: ${storeName} with configuration:`, config);
          } else {
            console.log(`Store already exists: ${storeName}`);
          }
        });
      },
      blocked() {
        console.warn('Database upgrade blocked - please close other tabs with this site open');
      },
      blocking() {
        console.warn('Database upgrade blocking - please close other tabs with this site open');
      },
    });
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error);
    throw new Error('Database initialization failed');
  }
};

export const idbStorage = {
  getItem: async (store: string, key: string) => {
    const db = await ensureDB();
    return (await db.get(store, key)) || null;
  },

  getAll: async (store: string) => {
    const db = await ensureDB();
    return (await db.getAll(store)) || null;
  },

  setItem: async (store: string, key: string, value: string) => {
    const db = await ensureDB();
    await db.put(store, value, key);
  },

  removeItem: async (store: string, key: string) => {
    const db = await ensureDB();
    await db.delete(store, key);
  },
  clear: async (store: string) => {
    const db = await ensureDB();
    await db.clear(store);
  },
  getAllKeys: async (store: string) => {
    const db = await ensureDB();
    return await db.getAllKeys(store);
  },

  hasRecords: async (store: string): Promise<boolean> => {
    const db = await ensureDB();
    const count = await db.count(store);
    return count > 0;
  },

  removeMultipleItems: async (store: string, keys: string[]) => {
    const db = await ensureDB();
    const transaction = db.transaction(store, 'readwrite');
    const objectStore = transaction.objectStore(store);

    for (const key of keys) {
      await objectStore.delete(key);
      console.log(`Deleted key: ${key}`);
    }

    await transaction.done; // Ensure the transaction completes
    console.log(`All specified keys removed from store: ${store}`);
  },
};
